<template>
  <div class="d-box">
    <div class="user-tasks__header">
      <div>
        <label class="d-label no-print" v-if="isShowScheduled">
          {{ $t('vue2.task_overview.showing_scheduled_hours_from') }} {{ dates }} ({{ numberOfWorkDaysInPeriod }}
          {{ i18n['work_days'] }})
        </label>
        <h4 class="only-print">{{ user.name }}, {{ dates }}</h4>
      </div>
      <div class="d-input-group no-print no-mobile" v-if="isShowHeader">
        <label class="d-label" style="margin-bottom: 6px;">{{ $t('vue2.task_overview.show_scheduled_hours') }} </label>
        <div @click.stop.prevent="setIsShowScheduled(!isShowScheduled)">
          <app-toggle :propOn="isShowScheduled"></app-toggle>
        </div>
      </div>
    </div>
    <table class="d-table marg-1-bottom d-table__overview" v-if="userNorm.length > 0">
      <thead>
      <tr>
        <th></th>
        <th class="is-number">Timer</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in userNormFormatted" v-if="idx < userNorm.length - 1" :key="item.title">
        <td>{{ item.title }}</td>
        <td class="is-number">{{ item.value }}</td>
      </tr>
      </tbody>
      <tfoot>
      <tr v-for="(item, idx) in userNormFormatted" v-if="idx === userNorm.length - 1" :key="item.title">
        <td>{{ item.title }}</td>
        <td class="is-number">{{ item.value }}</td>
      </tr>
      </tfoot>
    </table>
    <UserTasksOverviewKindergarten
      v-if="isKindergarten"
      :isShowScheduled="isShowScheduled"
      :isDateIntervalChanged="isDateIntervalChanged"
      :assignedTotalHoursForYear="getUserHoursOverview.assigned_total_hours"
      :assignedTotalHoursForPeriod="assignedTotalHoursForPeriod"
      :diffTotalHoursForPeriod="diffTotalHoursForPeriod"
      :userActualHours="userActualHours"
      :getUserHoursOverview="getUserHoursOverview"
      :tooltipContent="tooltipContent"
      :numberOfWorkDays="numberOfWorkDays"
      :numberOfWorkDaysInPeriod="numberOfWorkDaysInPeriod">
    </UserTasksOverviewKindergarten>
    <table class="d-table marg-1-bottom d-table__overview" v-if="!isKindergarten">
      <thead>
      <tr>
        <th><h4>{{ $t('vue2.task_overview.distribution') }}</h4></th>
        <th class="is-number"></th>
        <th class="is-number">{{ $t('vue2.assign_users_hours.assigned') }}</th>
        <th class="is-number" v-if="isShowScheduled">{{ $t('vue2.assign_users_hours.scheduled') }}</th>
        <th class="is-number" v-if="isShowScheduled">{{ $t('vue2.assign_users_hours.diff') }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="getIsCategoryVisible('work_hours')">
        <td>{{ $t('vue2.task_overview.work_hours') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">100%</div>
        </td>
        <td class="is-number"
            :class="{'is-number--calculated': isDateIntervalChanged }"
            :content='schoolTooltipContent'
            v-tippy="{
            animateFill: false,
            animation : 'shift-away',
          onShow: () => isDateIntervalChanged
        }">
          <span class="icon-clock" v-if="isDateIntervalChanged"></span>
          {{ assignedTotalHoursForPeriod }}
        </td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_total_hours }}</td>
        <td class="is-number" v-if="isShowScheduled" :style="scaleIndicator(diffTotalHoursForPeriod)">
          {{ diffTotalHoursForPeriod }}
        </td>
      </tr>
      <tr v-if="getIsCategoryVisible('teaching')">
        <td>{{ $t('vue2.task_overview.teaching') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfTeaching }}</div>
        </td>
        <td class="is-number">{{ getUserHoursOverview.assigned_teaching_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_teaching_hours }}</td>
        <td class="is-number" v-if="isShowScheduled" :style="scaleIndicator(getUserHoursOverview.diff_teaching_hours)">
          {{ getUserHoursOverview.diff_teaching_hours }}
        </td>
      </tr>
      <tr v-if="getIsCategoryVisible('tasks')">
        <td>{{ $t('vue2.task_overview.tasks') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfTasks }}</div>
        </td>
        <td class="is-number">{{ getUserHoursOverview.assigned_additional_task_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_additional_task_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">
          {{ getUserHoursOverview.diff_additional_task_hours }}
        </td>
      </tr>
      <tr v-if="getIsCategoryVisible('preparation')">
        <td>{{ $t('vue2.task_overview.preparation') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfPrep }}</div>
        </td>
        <td class="is-number">{{ getUserHoursOverview.assigned_preparation_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_preparation_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">
          {{ getUserHoursOverview.diff_preparation_hours }}
        </td>
      </tr>
      <tr v-if="getUserHoursOverview.assigned_pool_hours > 0 && getIsCategoryVisible('pool')">
        <td>{{ $t('vue2.task_overview.pool') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfPool }}</div>
        </td>
        <td class="is-number">{{ getUserHoursOverview.assigned_pool_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_pool_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">
          {{ getUserHoursOverview.diff_pool_hours }}
        </td>
      </tr>
      <tr v-if="getUserHoursOverview.substitute_pool_hours > 0 && getIsCategoryVisible('substitute_hours')">
        <td>{{ $t('vue2.assign_users_hours.substitute_hours') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfSubPool }}</div>
        </td>
        <td class="is-number">{{ getUserHoursOverview.substitute_pool_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_substitute_hours }}</td>
        <td class="is-number" v-if="isShowScheduled"
            :style="scaleIndicator(getUserHoursOverview.diff_substitute_hours)">
          {{ getUserHoursOverview.diff_substitute_hours }}
        </td>
      </tr>
      <tr v-if="getUserHoursOverview.assigned_club_hours > 0 && getIsCategoryVisible('club')">
        <td>{{ $t('vue2.task_overview.club') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfClub }}</div>
        </td>
        <td class="is-number">{{ getUserHoursOverview.assigned_club_hours }}</td>
        <td class="is-number" v-if="isShowScheduled">{{ getUserHoursOverview.actual_club_hours }}</td>
        <td class="is-number" v-if="isShowScheduled"
            :style="scaleIndicator(getUserHoursOverview.diff_club_hours)">
          {{ getUserHoursOverview.diff_club_hours }}
        </td>
      </tr>
      <tr v-if="isSchoolGlHjortespring && getIsCategoryVisible('teaching_and_club')">
        <td>Undervisning + SFO</td>
        <td class="is-number"></td>
        <td class="is-number">
          {{ getUserHoursOverview.assigned_teaching_hours + getUserHoursOverview.assigned_club_hours }}
        </td>
        <td class="is-number" v-if="isShowScheduled">
          {{ getUserHoursOverview.actual_teaching_hours + getUserHoursOverview.actual_club_hours }}
        </td>
        <td class="is-number" v-if="isShowScheduled"
            :style="scaleIndicator((getUserHoursOverview.assigned_teaching_hours + getUserHoursOverview.assigned_club_hours) - (getUserHoursOverview.actual_teaching_hours + getUserHoursOverview.actual_club_hours))">
          {{
            toFixedIfNecessary((getUserHoursOverview.assigned_club_hours + getUserHoursOverview.assigned_teaching_hours) - (getUserHoursOverview.actual_teaching_hours + getUserHoursOverview.actual_club_hours))
          }}
        </td>
      </tr>

      <tr v-if="isShowSubsAndTeachingActive && getIsCategoryVisible('teaching_and_sub')">
        <td>{{ titleForTeachingAndSubstituteHoursTotal }}</td>
        <td class="is-number"></td>
        <td class="is-number">
          {{ getUserHoursOverview.substitute_pool_hours + getUserHoursOverview.assigned_teaching_hours }}
        </td>
        <td class="is-number" v-if="isShowScheduled">
          {{
            toFixedIfNecessary(getUserHoursOverview.actual_teaching_hours + getUserHoursOverview.actual_substitute_hours)
          }}
        </td>
        <td class="is-number" v-if="isShowScheduled"
            :style="scaleIndicator((getUserHoursOverview.substitute_pool_hours + getUserHoursOverview.assigned_teaching_hours) - (getUserHoursOverview.actual_teaching_hours + getUserHoursOverview.actual_substitute_hours))">
          {{
            toFixedIfNecessary((getUserHoursOverview.substitute_pool_hours + getUserHoursOverview.assigned_teaching_hours) - (getUserHoursOverview.actual_teaching_hours + getUserHoursOverview.actual_substitute_hours))
          }}
        </td>
      </tr>
      <!-- REST -->
      <tr v-if="!isSchoolWantSum && getUserAssignedTotalHours > 0 && getTotalAssigned > 0">
        <td>{{ $t('vue2.task_overview.rest') }}</td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfRest }}</div>
        </td>
        <td class="is-number">{{ toFixedIfNecessary(getTotalAssigned) }}</td>
        <td class="is-number" v-if="isShowScheduled"></td>
        <td class="is-number" v-if="isShowScheduled">
        </td>
      </tr>
      <tr v-if="getTotalTooMany > 0 && !isSchoolWantSum && getUserAssignedTotalHours > 0">
        <td><span class="d-bagde d-badge--warning">{{ $t('vue2.task_overview.diff_from_total_workhours') }}</span></td>
        <td class="is-number">
          <div v-if="getUserAssignedTotalHours > 0">{{ getPercentageOfTooMany }}</div>
        </td>
        <td class="is-number">{{ toFixedIfNecessary(getTotalTooMany) }}</td>
        <td class="is-number" v-if="isShowScheduled"></td>
        <td class="is-number" v-if="isShowScheduled">
        </td>
      </tr>
      </tbody>
      <tfoot v-if="isSchoolWantSum">
      <tr>
        <td>Sum</td>
        <td class="is-number">
        </td>
        <td class="is-number cursor-help"
            content="Sum = (Skemalagt UV + Skemalagt SFO + Tildelte Øvrige Opgaver, Tildel Forberedelse osv.) - Total tildelt arbejdstid"
            v-tippy="{
            animateFill: false,
            animation : 'shift-away'
        }"><span v-if="getSum > 0">+</span>{{ toFixedIfNecessary(getSum) }}
        </td>
        <td class="is-number" v-if="isShowScheduled"></td>
        <td class="is-number" v-if="isShowScheduled"></td>
      </tr>
      </tfoot>
    </table>
    <UserTasksOverviewOwnPools
      v-if="schoolSettingShownOwnPools.length > 0"
      :schoolSettingShownOwnPools="schoolSettingShownOwnPools"
      :isShowScheduled="isShowScheduled"
      :isDateIntervalChanged="isDateIntervalChanged"
      :userTasks="userTasksFiltered">
    </UserTasksOverviewOwnPools>
    <div v-for="tasks in userTasksFiltered" class="marg-1-bottom">
      <UserTasksTableSubstituteHours
        v-if="tasks.title === 'substitute_hours'"
        :title="tasks.title"
        :tasks="tasks.tasks"
        :userCalendarId="user.user_calendar_id"
        :userId="user.id"
        :assignedHours="getUserAssignedTeachingHours"
        :isShowScheduled="isShowScheduled">
      </UserTasksTableSubstituteHours>
      <UserTasksTable
        v-else
        :title="tasks.title"
        :tasks="tasks.tasks"
        :userCalendarId="user.user_calendar_id"
        :userId="user.id"
        :assignedHours="getUserAssignedTeachingHours"
        :isShowScheduled="isShowScheduled">
      </UserTasksTable>
    </div>
    <div class="active-user__agreements">
      <SubjectsDistributionUserAgreements
        :isReadAgreements="false"
        :userAgreements="userAgreements"
        :userId="activeUserId">
      </SubjectsDistributionUserAgreements>
    </div>
    <transition name="d-modal">
      <DModal v-if="theActiveSubject" :showClose="false" @close="theActiveSubject = null" style="border-radius: 4px">
        <SubjectsDistributionUserEditHours
          slot="insertComponent"
          :subject="getTheActiveSubject"
          :teacher="getTheActiveUser"
          @close="theActiveSubject = null"/>
      </DModal>
    </transition>
    <div class="bg-overlay"
         v-if="theActiveSubject"
         @click="theActiveSubject = null">
    </div>
  </div>
</template>

<script>
import SubjectsDistributionUserAgreements from '../SubjectsDistribution/SubjectsDistributionUserAgreements'
import SubjectsDistributionUserEditHours from '../SubjectsDistribution/SubjectsDistributionUserEditHours'
import DDropdown from '../common/DDropdown'
import DModal from '../common/DModal'
import UserTasksTable from './UserTasksTable'
import AppToggle from '../common/AppToggle'
import UserTasksChart from './UserTasksChart'
import docendo from '../../docendo'
import moment from 'moment'
import UserTasksOverviewKindergarten from './UserTasksOverviewKindergarten'
import UserTasksTableSubstituteHours from './UserTasksTableSubstituteHours.vue'
import UserTasksOverviewOwnPools from './UserTasksOverviewOwnPools.vue'

const I18N = {
  da: {
    work_days: 'arbejdsdage',
    tooltip: {
      hours: 'timer',
      days: 'dage',
      work_days: 'arbejdsdage',
      per_day: 'pr. dag',
      calculation: 'Beregning',
      forYear: 'For året:',
      forPeriod: 'For perioden:',
      explanation: '<p><i>Når du vælger et andet datointerval end hele perioden, kan du her se, hvor mange timer, der i gennemsnit skal planlægges i perioden og per dag. Udregningen er baseret på antal arbejdsdage angivet for personalet, fratrukket weekender, feriedage og helligdage i \'Feriekalender\'</p>'
    }
  },
  nb: {
    work_days: 'arbeidsdager',
    tooltip: {
      hours: 'timer',
      days: 'dager',
      per_day: 'per dag',
      work_days: 'arbeidsdager',
      calculation: 'Beregning',
      forYear: 'For året:',
      forPeriod: 'For perioden:',
      explanation: '<p><i>Når du velger et annet datointerval enn hele året, kan du her se, hvor mange timer som skal planlegges i perioden og i gjennomsnitt per dag. Beregningen trekker helger og ferie- og helligdager fra.</i></p>'
    }
  },
  en: {
    work_days: 'work days',
    tooltip: {
      hours: 'hours',
      days: 'days',
      per_day: 'per day',
      work_days: 'work days',
      calculation: 'Calculation',
      forYear: 'For the year:',
      forPeriod: 'For the period:',
      explanation: '<p><i>When you choose a different date range than the whole year, you can see here how many hours need to be planned in the period and on average per day. The calculation subtracts weekends and holidays.</i></p>'
    }
  }
}

export default {
  components: {
    UserTasksOverviewOwnPools,
    UserTasksTableSubstituteHours,
    UserTasksOverviewKindergarten,
    AppToggle,
    SubjectsDistributionUserAgreements,
    DDropdown,
    DModal,
    SubjectsDistributionUserEditHours,
    UserTasksTable,
    UserTasksChart
  },
  props: {
    user: {
      default: () => {
        return {
          name: 'Henrik Rasch',
          id: 13,
          user_calendar_id: 7
        }
      }
    },
    isKindergarten: {
      type: Boolean,
      default: false
    },
    activeUserId: {
      default: null
    },
    showCloseCross: {
      default: true
    },
    isInUserCalendar: {
      default: false
    },
    dates: {},
    startDate: {
      default: () => moment()
    },
    endDate: {
      default: () => moment()
    },
    isShowHeader: {
      type: Boolean,
      default: true
    },
    isPoolsDeactivated: {
      type: Boolean,
      default: false
    },
    schoolPrepFactor: {
      default: null
    }
  },
  data () {
    return {
      isPrint: false,
      theActiveSubject: null,
      isShowScheduled: true,
      userNorm: [],
      userAgreements: '',
      allVacationsDays: [],
      userIdsWithoutCalc: [171563]
    }
  },
  computed: {
    hiddenTaskDistributionCategories () {
      return this.$store.getters.hiddenTaskDistributionCategories
    },
    schoolSettingShownOwnPools () {
      return this.$store.getters.shownOwnPools
    },
    titleForTeachingAndSubstituteHoursTotal () {
      const idx = this.userTasksFiltered.findIndex(userTasks => userTasks.title === 'substitute_hours')
      if (idx < 0) {
        return 'Undervisning + Vikartimer (UV-timer)'
      }
      const hasClubTasks = this.userTasksSortedInTypes[idx]['tasks'].some(task => task.master_type === 'club')
      if (hasClubTasks) {
        return 'Undervisning + Vikartimer (UV-timer & SFO)'
      } else {
        return 'Undervisning + Vikartimer (UV-timer)'
      }
    },
    userNormFormatted () {
      const normsFiltered = this.userNorm.filter(item => item.title.indexOf('hidden_') < 0)
      return normsFiltered.map(item => ({
        ...item,
        value: item.value
      }))
    },
    tooltipContent () {
      return `<p><i>Når du vælger et andet datointerval end hele året, kan du her se, hvor mange timer, der skal planlægges for medarbejderen i perioden og i gennemsnit per dag. Udregningen tager højde for institutionens ferie- og helligdage.</i></p><br></p><p><b>Beregning</b></p><p>For året: ${this.getUserHoursOverview.assigned_total_hours} timer / ${this.workDayDates.length} dage = ${this.averageWorkingHoursPerDay} timer pr. dag</p><p>For perioden: ${this.numberOfWorkDaysInPeriod} arbejdsdage * ${this.averageWorkingHoursPerDay} timer pr. dag = ${this.assignedTotalHoursForPeriod} timer</p>`
    },
    schoolTooltipContent () {
      const sumForYear = this.getUserHoursOverview.assigned_total_hours
      const hoursInYear = this.numberOfWorkDays
      const hoursPerDay = sumForYear / hoursInYear
      const hoursForPeriod = hoursPerDay * this.numberOfWorkDaysInPeriod
      const hoursPerDayFixed = this.toFixedIfNecessary(hoursPerDay)
      const hoursForPeriodFixed = this.toFixedIfNecessary(hoursForPeriod)
      return `${this.i18n.tooltip.explanation}<br></p><p><b>${this.i18n.tooltip.calculation}</b></p><p>${this.i18n.tooltip.forYear} ${sumForYear} ${this.i18n.tooltip.hours} / ${hoursInYear} ${this.i18n.tooltip.days} = ${hoursPerDayFixed} ${this.i18n.tooltip.hours} ${this.i18n.tooltip.per_day}</p><p>${this.i18n.tooltip.forPeriod} ${this.numberOfWorkDaysInPeriod} ${this.i18n.tooltip.work_days} * ${hoursPerDayFixed} ${this.i18n.tooltip.hours} ${this.i18n.tooltip.per_day} = ${hoursForPeriodFixed} ${this.i18n.tooltip.hours}</p>`
    },
    urlLocale () {
      const url = window.location.host
      let locale = 'en'
      if (url.indexOf('.dk') > -1) {
        locale = 'da'
      }
      if (url.indexOf('.no') > -1) {
        locale = 'nb'
      }
      return locale
    },
    locale () {
      return window.USER && window.USER.language ? window.USER.language : this.urlLocale
    },
    i18n () {
      return I18N[this.locale]
    },
    isDateIntervalChanged () {
      const userId = this.user.id * 1
      if (this.userIdsWithoutCalc.indexOf(userId) > -1) {
        return false
      }
      const originalStart = moment(this.activeSchoolYear.employee_time_start).format('YYYY-MM-DD')
      const originalEnd = moment(this.activeSchoolYear.employee_time_end).format('YYYY-MM-DD')
      const startDate = moment(this.startDate).format('YYYY-MM-DD')
      const endDate = moment(this.endDate).format('YYYY-MM-DD')
      return (startDate !== originalStart) || (endDate !== originalEnd)
    },
    schoolId () {
      if (window.SCHOOL && window.SCHOOL.id) {
        return window.SCHOOL.id
      }
      if (this.$route && this.$route.params && this.$route.params.school_id) {
        return this.$route.params.school_id
      }
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    workDayDates () {
      const self = this

      function isNotWeekend (m) {
        return moment(m).isoWeekday() !== 6 && moment(m).isoWeekday() !== 7
      }

      function isNotVacationDay (m) {
        const idx = self.allVacationsDays.findIndex(date => moment(date.day).format('YYYY-MM-DD') === m)
        return idx < 0
      }

      let startDate = moment(this.activeSchoolYear.employee_time_start).format('YYYY-MM-DD')
      const endDate = moment(this.activeSchoolYear.employee_time_end).format('YYYY-MM-DD')
      const dateArray = []
      while (moment(startDate).isSameOrBefore(endDate)) {
        const m = moment(startDate).format('YYYY-MM-DD')
        if (isNotWeekend(m) && isNotVacationDay(m)) {
          dateArray.push(m)
        }
        startDate = moment(startDate).add(1, 'days')
      }
      return dateArray
    },
    workDayDatesInPeriod () {
      let startDate = moment(this.startDate).format('YYYY-MM-DD')
      const endDate = moment(this.endDate).format('YYYY-MM-DD')
      const dateArray = []
      while (moment(startDate).isSameOrBefore(endDate)) {
        const m = moment(startDate).format('YYYY-MM-DD')
        dateArray.push(m)
        startDate = moment(startDate).add(1, 'days')
      }
      return dateArray.filter(date => {
        const m = moment(date).format('YYYY-MM-DD')
        const idx = this.workDayDates.indexOf(m)
        return idx > -1
      })
    },
    numberOfWorkDays () {
      return this.workDayDates.length
    },
    numberOfWorkDaysInPeriod () {
      return this.workDayDatesInPeriod.length
    },
    averageWorkingHoursPerDay () {
      const hoursPerDay = (this.getUserHoursOverview.assigned_total_hours / this.numberOfWorkDays) * 1
      return this.toFixedIfNecessary(hoursPerDay)
    },
    assignedTotalHoursForPeriod () {
      const userId = this.user.id * 1
      if (this.userIdsWithoutCalc.includes(userId)) {
        return this.getUserHoursOverview.assigned_total_hours
      }
      const hoursPerDay = (this.getUserHoursOverview.assigned_total_hours / this.numberOfWorkDays) * 1
      const hoursInThePeriod = hoursPerDay * this.numberOfWorkDaysInPeriod
      return this.toFixedIfNecessary(hoursInThePeriod)
    },
    diffTotalHoursForPeriod () {
      const diff = this.assignedTotalHoursForPeriod - this.getUserHoursOverview.actual_total_hours
      return this.toFixedIfNecessary(diff)
    },
    schoolCountry () {
      return this.$store.getters.schoolCountry
    },
    isShowSubsAndTeachingActive () {
      const schoolIds = [7272, 1256, 2987, 2991, 2986, 2989, 2990, 2985, 2992, 4910, 4911, 4912, 1976, 6527, 61476, 60668, 1795, 60800]
      return schoolIds.indexOf(this.schoolId) > -1
    },
    isSchoolGlHjortespring () {
      return this.schoolId === 25609
    },
    isSchoolWantSum () {
      const schoolIds = [40195]
      return schoolIds.indexOf(this.schoolId) > -1
    },
    userTasksSortedInTypes () {
      return this.$store.getters.userTasksSortedInTypes
    },
    userTasksFiltered () {
      if (this.isShowScheduled) {
        return this.userTasksSortedInTypes
      } else {
        return this.userTasksSortedInTypes.filter(type => type.title !== 'absence' && type.title !== 'substitute_hours')
      }
    },
    getTheActiveSubject () {
      return this.theActiveSubject ? this.subjectsInHash[this.theActiveSubject] : null
    },
    getTheActiveUser () {
      return this.activeUserId ? this.allUsersInHash[this.activeUserId] : null
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    subjectsInUserHash () {
      return this.$store.getters.subjectsInUserHash
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    usersAssignedHoursHash () {
      return this.$store.getters.usersAssignedHoursHash
    },
    preparationHoursInUserHash () {
      return this.$store.getters.preparationHoursInUserHash
    },
    localPoolHoursInUserHash () {
      return this.$store.getters.localPoolHoursInUserHash
    },
    clubHoursInUserHash () {
      return this.$store.getters.clubHoursInUserHash
    },
    theUserAssignedHours () {
      // Hent objektet fra usersAssignedHoursHash eller lav et tomt objekt
      const obj = this.usersAssignedHoursHash[this.user.id]
        ? {...this.usersAssignedHoursHash[this.user.id]} // Kopierer objektet for at undgå mutation
        : {}
      if (this.isPoolsDeactivated) {
        const calId = this.user.user_calendar_id
        const assignedTeachingHours = this.getSumOfHoursForMasterType('teaching', calId)
        obj.assigned_teaching_hours = assignedTeachingHours || 0
        obj.assigned_additional_task_hours = this.getSumOfHoursForMasterType('additional_tasks')
        obj.assigned_preparation_hours =
          this.schoolPrepFactor !== null
            ? assignedTeachingHours * this.schoolPrepFactor
            : this.getSumOfHoursForMasterType('preparation') || 0
        obj.assigned_pool_hours = this.getSumOfHoursForMasterType('pool') || 0
        obj.assigned_club_hours = this.getSumOfHoursForMasterType('club') || 0
      }
      return obj
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    tasksInUserHash () {
      return this.$store.getters.tasksInUserHash
    },
    tasksInHash () {
      return this.$store.getters.tasksInHash
    },
    allUserTasks () {
      return this.tasksInUserHash[this.user.user_calendar_id] ? this.tasksInUserHash[this.user.user_calendar_id].map(taskId => this.tasksInHash[taskId]) : []
    },
    getAllUserTasksTask () {
      return this.allUserTasks.filter(task => task.subject_type === 'additional_tasks')
    },
    userActualHours () {
      return this.$store.state.subjects.userActualHours
    },
    getUserHoursOverview () {
      const obj = this.user
      const usersWithAssignedHours = this.theUserAssignedHours
      const usersWithScheduledHours = this.userActualHours
      const user = {
        id: obj.id,
        name: obj.name,
        assigned_total_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.assigned_total_hours)) || 0,
        actual_total_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_total_hours)) || 0,
        diff_total_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.assigned_total_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_total_hours) || 0)),
        assigned_teaching_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.assigned_teaching_hours)) || 0,
        actual_teaching_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_teaching_hours)) || 0,
        diff_teaching_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.assigned_teaching_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_teaching_hours) || 0)),
        substitute_pool_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.substitute_pool_hours)) || 0,
        actual_substitute_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_substitute_hours)) || 0,
        diff_substitute_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.substitute_pool_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_substitute_hours) || 0)),
        actual_absence_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_absence_hours)) || 0,
        assigned_additional_task_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.assigned_additional_task_hours)) || 0,
        actual_additional_task_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_additional_task_hours)) || 0,
        diff_additional_task_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.assigned_additional_task_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_additional_task_hours) || 0)),
        assigned_preparation_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.assigned_preparation_hours)) || 0,
        actual_preparation_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_preparation_hours)) || 0,
        diff_preparation_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.assigned_preparation_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_preparation_hours) || 0)),
        assigned_pool_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.assigned_pool_hours)) || 0,
        actual_pool_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_pool_hours)) || 0,
        diff_pool_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.assigned_pool_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_pool_hours) || 0)),
        assigned_club_hours: (usersWithAssignedHours && this.toFixedIfNecessary(usersWithAssignedHours.assigned_club_hours)) || 0,
        actual_club_hours: (usersWithScheduledHours && this.toFixedIfNecessary(usersWithScheduledHours.actual_club_hours)) || 0,
        diff_club_hours: this.toFixedIfNecessary(parseFloat((usersWithAssignedHours && usersWithAssignedHours.assigned_club_hours) || 0) - parseFloat((usersWithScheduledHours && usersWithScheduledHours.actual_club_hours) || 0))
      }
      return user
    },
    getUserAssignedTotalHours () {
      return this.theUserAssignedHours.assigned_total_hours || 0
    },
    getUserAssignedTeachingHours () {
      return this.theUserAssignedHours.assigned_teaching_hours || 0
    },
    getPercentageOfTeaching () {
      let total = this.getUserAssignedTotalHours
      let teaching = this.getUserAssignedTeachingHours
      let percentage = (teaching / total * 100)
      return percentage.toFixed(0) + '%'
    },
    getUserAssignedTaskHours () {
      return this.theUserAssignedHours.assigned_additional_task_hours || 0
    },
    getPercentageOfTasks () {
      let total = this.getUserAssignedTotalHours
      let tasks = this.getUserAssignedTaskHours
      let percentage = (tasks / total * 100)
      return percentage.toFixed(0) + '%'
    },
    getUserAssignedPreparationHours () {
      return this.theUserAssignedHours.assigned_preparation_hours || 0
    },
    getUserAssignedClubHours () {
      return this.theUserAssignedHours.assigned_club_hours || 0
    },
    getPercentageOfPrep () {
      let total = this.getUserAssignedTotalHours
      let tasks = this.getUserAssignedPreparationHours
      let percentage = (tasks / total * 100)
      return percentage.toFixed(0) + '%'
    },
    getUserAssignedPoolHours () {
      return this.theUserAssignedHours.assigned_pool_hours || 0
    },
    getPercentageOfPool () {
      let total = this.getUserAssignedTotalHours
      let tasks = this.getUserAssignedPoolHours
      let percentage = (tasks / total * 100)
      return percentage.toFixed(0) + '%'
    },
    getPercentageOfClub () {
      let total = this.getUserAssignedTotalHours
      let tasks = this.getUserAssignedClubHours
      let percentage = (tasks / total * 100)
      return percentage.toFixed(0) + '%'
    },
    getPercentageOfSubPool () {
      let total = this.getUserAssignedTotalHours
      let tasks = this.getUserHoursOverview.substitute_pool_hours
      let percentage = (tasks / total * 100)
      return percentage.toFixed(0) + '%'
    },
    userSettings () {
      return this.$store.state.userSettings
    },
    getSum () {
      let total = this.getUserHoursOverview.assigned_total_hours
      let sumOfAllAssigned = this.getUserHoursOverview.actual_teaching_hours + this.getUserHoursOverview.actual_club_hours + this.getUserHoursOverview.assigned_additional_task_hours + this.getUserHoursOverview.assigned_preparation_hours + this.getUserHoursOverview.substitute_pool_hours + this.getUserHoursOverview.assigned_pool_hours
      return sumOfAllAssigned - total
    },
    getTotalAssigned () {
      const total = this.getUserHoursOverview.assigned_total_hours
      const sumOfAllAssigned = this.getUserHoursOverview.assigned_teaching_hours + this.getUserHoursOverview.assigned_additional_task_hours + this.getUserHoursOverview.assigned_preparation_hours + this.getUserHoursOverview.substitute_pool_hours + this.getUserHoursOverview.assigned_club_hours + this.getUserHoursOverview.assigned_pool_hours
      return total - sumOfAllAssigned
    },
    getPercentageOfRest () {
      const total = this.getUserHoursOverview.assigned_total_hours
      const percentage = (this.getTotalAssigned / total * 100)
      return percentage.toFixed(0) + '%'
    },
    getTotalTooMany () {
      const total = this.getUserHoursOverview.assigned_total_hours
      const sumOfAllAssigned = this.getUserHoursOverview.assigned_teaching_hours + this.getUserHoursOverview.assigned_additional_task_hours + this.getUserHoursOverview.assigned_preparation_hours + this.getUserHoursOverview.substitute_pool_hours + this.getUserHoursOverview.assigned_club_hours + this.getUserHoursOverview.assigned_pool_hours
      return sumOfAllAssigned - total
    },
    getPercentageOfTooMany () {
      const total = this.getUserHoursOverview.assigned_total_hours
      const percentage = (this.getTotalTooMany / total * 100)
      return percentage.toFixed(0) + '%'
    },
    tasksHoursInUserHash () {
      return this.$store.getters.tasksHoursInUserHash
    },
    userTasks () {
      return this.$store.state.subjects.userTasks
    }
  },
  watch: {
    userSettings () {
      if (!this.isShowHeader) {
        return
      }
      if (this.userSettings && this.userSettings.hasOwnProperty('userTasksIsShowScheduled')) {
        this.isShowScheduled = this.userSettings.userTasksIsShowScheduled
      }
    },
    activeSchoolYear () {
      this.readUserAgreements()
    }
  },
  methods: {
    getSumOfHoursForMasterType (subjectType) {
      const isTeaching = subjectType === 'teaching'
      const tasksFiltered = this.userTasks.filter(task =>
        isTeaching ? task.master_type === 'teaching' : task.subject_type === subjectType
      )
      const tasksHours = tasksFiltered.map(task =>
        task.hours ? parseFloat(task.hours) : 0
      )
      const sum = tasksHours.reduce((acc, item) => acc + item, 0)
      return this.toFixedIfNecessary(sum, 2)
    },
    getIsCategoryVisible (category) {
      return this.hiddenTaskDistributionCategories && this.hiddenTaskDistributionCategories.indexOf(category) < 0
    },
    getSchoolDays () {
      docendo.schoolVacationDaysRead(this.schoolId, success => {
        if (success.status !== 'error') {
          this.allVacationsDays = success
        }
      }, failure => {
        console.log('failure')
      })
    },
    toFixedIfNecessary (value) {
      // https://stackoverflow.com/questions/32229667/have-max-2-decimal-places/32229831
      return +parseFloat(value).toFixed(2)
    },
    setIsShowScheduled (boolean) {
      this.isShowScheduled = boolean
      this.$emit('changedIsShowScheduled', boolean)
      let obj = {
        userTasksIsShowScheduled: boolean
      }
      this.$store.dispatch('updateUserSettings', obj)
    },
    scaleIndicator (diff) {
      let color = 'rgba(62, 207, 142, 0.4)'
      let newDiff = parseFloat(diff)
      let indicatorPercentage = parseFloat(newDiff.toFixed(0))
      if (indicatorPercentage < 10 && indicatorPercentage > 0) {
        indicatorPercentage = '0' + indicatorPercentage
      }
      if (indicatorPercentage > -10 && indicatorPercentage < 0) {
        indicatorPercentage = '0' + (indicatorPercentage * -1)
      }
      if (indicatorPercentage < -9 && indicatorPercentage < 0) {
        indicatorPercentage = indicatorPercentage * -1
      }
      if (newDiff === 0) {
        color = 'transparent'
      }
      if (newDiff < 0) {
        color = 'rgba(250, 86, 90, 0.' + indicatorPercentage + ')'
      }
      if (newDiff < -99) {
        color = 'rgba(250, 86, 90, 1)'
      }
      if (newDiff > 0) {
        color = 'rgba(62, 207, 142, 0.' + indicatorPercentage + ')'
      }
      if (newDiff > 99) {
        color = 'rgba(62, 207, 142, 1)'
      }
      return {'backgroundColor': color}
    },
    getAllUserTasks (calid) {
      return this.tasksInUserHash[calid]
    },
    setEditSubject (editSubject) {
      this.$emit('editSubject', editSubject)
    },
    setDeleteTask (taskId) {
      let task = this.tasksInHash[taskId]
      task['itemType'] = 'task'
      this.$store.commit('setItemToDelete', task)
      this.$store.commit('setConfirmDeletion', true)
    },
    getTotalSubjectTeachingHours (userCalId) {
      let subjectsHours = this.subjectsInUserHash[userCalId] ? this.subjectsInUserHash[userCalId].map(subjectId => this.getSubjectHours(subjectId) * 1) : [0]
      let sum = subjectsHours.reduce((acc, item) => acc + item)
      return sum.toFixed(2)
    },
    getTotalUserTasksTeachingHours (userCalId) {
      let tasksHours = this.tasksInUserHash[userCalId] ? this.tasksInUserHash[userCalId].map(taskId => this.tasksInHash[taskId].hours_teaching * 1) : [0]
      return tasksHours.reduce((acc, item) => acc + item).toFixed(2)
    },
    setActiveSubject (subject) {
      this.theActiveSubject = subject
    },
    getSubjectHours (subjectId) {
      let theSubject = this.subjectsInHash[subjectId]
      let idx = theSubject.teachers[0] ? theSubject.teachers.findIndex(teacher => teacher.user_id === this.activeUserId) : -1
      if (idx > -1) {
        return theSubject.teachers[idx].hours
      } else {
        return theSubject.hours
      }
    },
    getIsHoursModified (subjectId) {
      let theSubject = this.subjectsInHash[subjectId]
      let idx = theSubject.teachers[0] ? theSubject.teachers.findIndex(teacher => teacher.user_id === this.activeUserId) : -1
      return idx > -1
    },
    readUserAgreements () {
      docendo.schoolUserYearRead(this.schoolId, this.user.id, this.activeSchoolYear.name, success => {
        this.userNorm = success.norm ? success.norm : []
        this.userAgreements = success.agreements ? success.agreements : ''
      }, failure => {
        console.log('failed!')
      })
    }
  },
  beforeMount () {
    if (!this.isShowHeader) {
      this.isShowScheduled = false
    }
  },
  mounted () {
    this.getSchoolDays()
    this.readUserAgreements()
    if (!this.isShowHeader) {
      return
    }
    if (this.userSettings && this.userSettings.hasOwnProperty('userTasksIsShowScheduled')) {
      this.isShowScheduled = this.userSettings.userTasksIsShowScheduled
      this.$emit('changedIsShowScheduled', this.isShowScheduled)
    }
  }
}
</script>


<style scoped>

.user-tasks__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.user-tasks__header .d-input-group {
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 2px;
}

.user-tasks__header .d-input-group.no-mobile {
  display: none;
}

@media only screen and (min-width: 768px) {
  .user-tasks__header .d-input-group.no-mobile {
    display: flex;
  }
}

.user-tasks__header .d-input-group .d-label {
  margin-right: 0.5rem;
}

.active-user {
  position: relative;
  width: 100%;
  margin-bottom: 20px;
}

.active-user h2 {
  margin-right: 20px;
}

.active-user__close-cross {
  position: absolute;
  z-index: 100;
  top: 0;
  right: 0;
  border-radius: 50%;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.active-user__close-cross:hover {
  background: #eee;
}

.d-table, .subjects-and-tasks {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;
}

.d-table > thead, .d-table > tfoot {
  border: 1px solid #e2e8f0;
  text-transform: uppercase;
  font-weight: bold;
  text-align: left;
  background: #f6f9fc;
}

.d-table > tfoot tr:last-child {
  font-size: 13px;
}

.d-table > thead > tr > th, .d-table > tfoot > tr > td {
  padding: 5px;
}

.d-table > thead > tr:first-child, .d-table > tfoot > tr:first-child {
  color: #8898aa;
}

.d-table tr th, .d-table > tfoot tr td {
  padding: 2px 4px;
}

.d-table tr th h4 {
  color: #3e4450;
}

.d-table tr td {
  padding: 0 4px;
}

.subjects_subject {
  position: relative;
  padding: 4px 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  text-align: left;
  margin: 0 5px 5px 0px;
  min-width: auto;
  font-weight: bold;
  transition: transform 0.25s;
  animation: fadeIn;
  cursor: pointer;
}

.is-number {
  text-align: right;
}

tbody tr:first-child td .subjects_subject {
  margin-top: 5px;
}

tbody tr:last-child td .subjects_subject {
  margin-bottom: 10px;
}

.active-user__agreements {
  margin-top: 10px;
}

.task-with-edit {
  display: flex;
  align-items: center;
  transform: translateX(-30px);
  transition: transform 0.25s;
}

.task-with-edit:hover {
  transform: translateX(0);
}

.task-with-edit .d-dropdown__task {
  opacity: 0;
  visibility: hidden;
  transition: all 0.25s;
}

.task-with-edit:hover .d-dropdown__task {
  opacity: 1;
  visibility: initial;
}

.subject-hours-for-person {
  position: relative;
  cursor: pointer;
  transition: transform 0.25s;
}

.subject-hours-for-person:hover {
  transform: translateY(-1px);
  text-decoration: underline;
  color: #468AD7;
}

.subject-hours-for-person.subject-hours-for-person--is-modified:after {
  content: "\e904";
  position: absolute;
  bottom: 10px;
  padding: 4px;
  left: 0px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  -webkit-font-feature-settings: normal;
  font-feature-settings: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.d-table__overview {
  font-size: 10px;
}

@media only screen and (min-width: 500px) {
  .d-table__overview {
    font-size: 1rem;
  }
}

.d-table__overview tbody tr {
  border: 1px solid #e2e8f0;
}

.d-table__overview tbody tr:nth-child(even) {
  background-color: #f8fafb;
}

.d-table__overview tbody tr td {
  padding: 0.25rem;
}

.d-table__overview tbody tr td:first-child {
  padding: 0.25rem;
}

.d-table__overview tbody tr td:nth-child(4) {
  padding-right: 1rem;
}

.d-table__subjects__user tbody tr:nth-child(even) {
  background-color: #f8fafb;
}

.d-table__subjects__user thead th:first-child,
.d-table__subjects__user tbody td:first-child {
  max-width: 300px;
  width: 20%
}

.d-table tbody td:nth-child(2) {
  width: 50%;
}

.d-table tbody td:nth-child(3),
.d-table tbody td:nth-child(4),
.d-table tbody td:nth-child(5) {
  width: 10%;
}

.no-pointer-events {
  position: relative;
  pointer-events: none;
  cursor: not-allowed;
}

.no-pointer-events > div,
.no-pointer-events > label {
  opacity: 0.5;
}

.no-pointer-events > .pill {
  line-height: 14px;
}

.only-print {
  display: none;
}

.is-number--calculated {
  color: #FC5F45FF;
  font-style: italic;
}

.is-number--calculated {
  color: var(--purple);
  font-style: italic;
  cursor: help;
}

@media print {
  .only-print {
    display: block;
  }
}

.d-bagde.d-badge--warning {
  border-radius: 4px;
  background-color: #fef3f2;
  border: 1px solid #fbdfde;
  color: #b91c1c;
  padding: 2px 4px;
  font-size: 10px;
}
</style>



// WEBPACK FOOTER //
// UserTasksOverview.vue?692ab7c3